/**
 * Emporia rebates & savings modal runtime.
 * Ported from the custom-liquid block that shipped on the pro-ev-meta PDP.
 * Required hooks: [data-emporia-rebate-overlay] wrapping [data-emporia-rebate-widget]
 * with data-plan="ev|fixed", plus [data-emporia-rebate-close],
 * [data-emporia-rebate-state-selector], [data-emporia-rebate-state],
 * [data-emporia-rebate-results], [data-emporia-rebate-loading] and
 * [data-emporia-rebate-progress].
 * Opens from any element with id="open-ev-rebate-widget"; the trigger is bound through
 * delegation so it can render before or after this section.
 */

(() => {
  'use strict';

  const API_URL =
    'https://script.google.com/macros/s/AKfycbxzwM67lDpqyQPrMNzRIRKBX5QiYY6FV1oiQZEedeSRdm_dRcqkb9mMS7z-5chSu9E3/exec';

  const EMPORIA_PLANS = {
    ev: {
      name: 'EV Charging Savings Plan',
      body: 'Whole-home electricity with free daily charging windows, automatically scheduled through your Emporia charger.',
      url: 'https://www.emporiaenergy.com/electricity-plans/ev-charging/?utm_source=shopify&utm_medium=web&utm_campaign=july30-new-customer&utm_content=shopify-savings-widget&utm_term=ev'
    },
    fixed: {
      name: 'Fixed electricity plan',
      body: 'A straightforward whole-home electricity plan with no tricks.',
      url: 'https://www.emporiaenergy.com/fixed-plan/?utm_source=shopify&utm_medium=web&utm_campaign=july30-new-customer&utm_content=shopify-savings-widget&utm_term=fixed'
    }
  };

  const TRIGGER_SELECTOR = '#open-ev-rebate-widget';
  const OVERLAY_SELECTOR = '[data-emporia-rebate-overlay]';
  const CLOSE_SELECTOR = '[data-emporia-rebate-close]';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'select:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* Matches the CSS opacity transition on .ev-rebate-popup-overlay. */
  const TRANSITION_MS = 300;

  const modals = new WeakMap();
  let activeModal = null;

  function setScrollLock(locked) {
    const method = locked ? 'add' : 'remove';
    document.documentElement.classList[method]('ev-rebate-popup-open');
    document.body.classList[method]('ev-rebate-popup-open');
  }

  /**
   * Escapes HTML characters in a string to prevent XSS.
   * @param {string} text The text to escape.
   * @returns {string} The escaped HTML string.
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /** Loads the rebate feed and renders program cards inside one widget. */
  class RebatePrograms {
    constructor(root, apiUrl) {
      this.root = root;
      this.apiUrl = apiUrl;
      this.data = null;
      this.init();
    }

    async init() {
      try {
        this.startLoadingProgress();
        await this.loadData();
        this.completeLoading();
      } catch (error) {
        this.showError('Failed to load rebate data. Please try again later.');
        console.error('[Emporia rebate widget]', error);
      }
    }

    get progressBar() {
      return this.root.querySelector('[data-emporia-rebate-progress]');
    }

    get resultsContainer() {
      return this.root.querySelector('[data-emporia-rebate-results]');
    }

    get stateSelect() {
      return this.root.querySelector('[data-emporia-rebate-state]');
    }

    /**
     * Animates the progress bar toward 95% while the fetch is in flight.
     */
    startLoadingProgress() {
      const progressBar = this.progressBar;
      if (!progressBar) return;

      let progress = 0;
      const preLoadTarget = 95;
      const progressStep = 0.5;

      const animateProgress = () => {
        if (progress < preLoadTarget) {
          progress += progressStep;
          progressBar.style.width = `${progress}%`;
          window.requestAnimationFrame(animateProgress);
        }
      };

      animateProgress();
    }

    /**
     * Completes the loading animation and reveals the state dropdown.
     */
    completeLoading() {
      const progressBar = this.progressBar;
      if (progressBar) progressBar.style.width = '100%';

      window.setTimeout(() => {
        const stateSelector = this.root.querySelector('[data-emporia-rebate-state-selector]');
        if (stateSelector) stateSelector.style.display = 'block';
        this.clearLoadingMessage();
        this.setupEventListeners();
        this.populateStateSelect();
      }, 500);
    }

    clearLoadingMessage() {
      const loadingMessage = this.root.querySelector('[data-emporia-rebate-loading]');
      if (loadingMessage) loadingMessage.remove();
    }

    async loadData() {
      const response = await fetch(this.apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.data = await response.json();

      if (this.data && this.data.error) {
        throw new Error(this.data.error);
      }
    }

    populateStateSelect() {
      const select = this.stateSelect;
      if (!select || !this.data || !this.data.states) return;

      select.innerHTML = '<option value="">Choose a state...</option>';

      Object.entries(this.data.states)
        .sort(([, a], [, b]) => a.name.localeCompare(b.name))
        .forEach(([code, stateData]) => {
          const programs = Array.isArray(stateData.programs) ? stateData.programs : [];
          const option = document.createElement('option');
          option.value = code;
          option.textContent = `${stateData.name} (${programs.length} programs)`;
          select.appendChild(option);
        });
    }

    setupEventListeners() {
      const select = this.stateSelect;
      if (!select || select.dataset.emporiaRebateBound === 'true') return;

      select.dataset.emporiaRebateBound = 'true';
      select.addEventListener('change', (event) => {
        this.displayResults(event.target.value);
      });
    }

    /**
     * Displays the rebate programs for the selected state.
     * Texas also pins an Emporia electricity plan card above utility programs.
     * @param {string} stateCode The code of the selected state (e.g., 'CA', 'NY').
     */
    displayResults(stateCode) {
      const container = this.resultsContainer;
      if (!container) return;

      if (!stateCode) {
        container.innerHTML =
          '<div class="loading">Select a state to see available rebates and incentives.</div>';
        return;
      }

      const isTexas = stateCode === 'TX';
      const states = (this.data && this.data.states) || {};
      const stateData = states[stateCode];
      const programs = stateData && stateData.programs ? stateData.programs : [];
      const parts = [];

      if (isTexas) {
        parts.push(this.createEmporiaPlanCard());
      }

      if (!programs.length) {
        if (!isTexas) {
          container.innerHTML =
            '<div class="no-results">No rebate programs found for this state.</div>';
          return;
        }
        /* TX with no utility programs: still show the pinned plan card. */
        container.innerHTML = parts.join('');
        return;
      }

      const sortedPrograms = [...programs].sort((a, b) => a.utility.localeCompare(b.utility));

      parts.push(...sortedPrograms.map((program) => this.createProgramCard(program)));
      container.innerHTML = parts.join('');
    }

    /**
     * Pinned Emporia electricity plan card for Texas.
     * Plan type comes from the widget's data-plan ("ev" | "fixed"), default ev.
     */
    createEmporiaPlanCard() {
      const planKey = this.root.dataset.plan === 'fixed' ? 'fixed' : 'ev';
      const plan = EMPORIA_PLANS[planKey];

      return `
        <div class="program-card emporia-plan-card">
          <div class="program-header">
            <span class="plan-badge">Emporia plan</span>
            <h3 class="program-name">${escapeHtml(plan.name)}</h3>
            <p class="utility-name">Available in Texas</p>
          </div>
          <div class="program-content">
            <p class="incentive-text">${escapeHtml(plan.body)}</p>
            <a href="${escapeHtml(plan.url)}" target="_blank" rel="noopener" class="program-link">
              Check Eligibility
            </a>
          </div>
        </div>
      `;
    }

    /**
     * Creates the HTML string for a single program card.
     * @param {object} program The program data object.
     * @returns {string} The HTML string for the program card.
     */
    createProgramCard(program) {
      const hasLink = program.link && program.link.trim();

      return `
        <div class="program-card">
          <div class="program-header">
            <h3 class="program-name">${escapeHtml(program.programName)}</h3>
            <p class="utility-name">${escapeHtml(program.utility)}</p>
          </div>
          <div class="program-content">
            ${
              program.customerIncentive
                ? `<p class="incentive-text">${escapeHtml(program.customerIncentive)}</p>`
                : ''
            }
            ${
              hasLink
                ? `<a href="${escapeHtml(program.link)}" target="_blank" rel="noopener" class="program-link">
                    View Program Details
                  </a>`
                : ''
            }
          </div>
        </div>
      `;
    }

    showError(message) {
      const container = this.resultsContainer;
      if (container) {
        container.innerHTML = `<div class="error-message">${escapeHtml(message)}</div>`;
      }
    }
  }

  /** Opens, closes and focus-traps one rebate overlay. */
  class RebateModal {
    constructor(overlay) {
      this.overlay = overlay;
      this.dialog = overlay.querySelector('[data-emporia-rebate-widget]');
      this.programs = null;
      this.trigger = null;
      this.hideTimer = null;
      this.isOpen = false;
    }

    open(trigger) {
      if (!this.dialog || this.isOpen) return;

      this.isOpen = true;
      this.trigger = trigger || null;
      activeModal = this;

      window.clearTimeout(this.hideTimer);
      setScrollLock(true);
      this.overlay.style.display = 'flex';

      /* Let the display change land before the opacity transition starts. */
      window.setTimeout(() => {
        this.overlay.classList.add('visible');
      }, 10);

      this.focusDialog();

      if (!this.programs) {
        this.programs = new RebatePrograms(this.dialog, API_URL);
      }
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.overlay.classList.remove('visible');
      setScrollLock(false);

      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        this.overlay.style.display = 'none';
      }, TRANSITION_MS);

      if (activeModal === this) activeModal = null;

      const trigger = this.trigger;
      this.trigger = null;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
    }

    focusDialog() {
      this.dialog.focus({ preventScroll: true });
    }

    get focusableElements() {
      return Array.prototype.filter.call(
        this.dialog.querySelectorAll(FOCUSABLE_SELECTOR),
        (element) => element.offsetParent !== null || element === document.activeElement
      );
    }

    trapFocus(event) {
      if (!this.dialog) return;

      const focusable = this.focusableElements;
      if (!focusable.length) {
        event.preventDefault();
        this.focusDialog();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;

      if (current === this.dialog || !this.dialog.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function register(overlay) {
    if (!overlay) return null;

    let modal = modals.get(overlay);
    if (!modal) {
      modal = new RebateModal(overlay);
      modals.set(overlay, modal);
    }

    return modal;
  }

  function initialize(root) {
    const scope = root && root.querySelectorAll ? root : document;

    if (scope.matches && scope.matches(OVERLAY_SELECTOR)) register(scope);
    scope.querySelectorAll(OVERLAY_SELECTOR).forEach(register);
  }

  function releaseActiveModal() {
    setScrollLock(false);
    activeModal = null;
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      const modal = register(document.querySelector(OVERLAY_SELECTOR));
      if (!modal) return;

      event.preventDefault();
      modal.open(trigger);
      return;
    }

    const closeButton = target.closest(CLOSE_SELECTOR);
    if (closeButton) {
      const modal = modals.get(closeButton.closest(OVERLAY_SELECTOR));
      if (modal) modal.close();
      return;
    }

    /* Overlay clicks only close when the backdrop itself was clicked. */
    if (target.matches(OVERLAY_SELECTOR)) {
      const modal = modals.get(target);
      if (modal) modal.close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      activeModal.close();
      return;
    }

    if (event.key === 'Tab') {
      activeModal.trapFocus(event);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize(document);
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (activeModal && !document.contains(activeModal.overlay)) {
      releaseActiveModal();
    }

    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target instanceof Element ? event.target : null;
    if (!activeModal || !section) return;

    if (section.contains(activeModal.overlay)) {
      releaseActiveModal();
    }
  });
})();
